<template>
  <div style="max-width: 890px; margin-bottom: 50px;">
    <h2 class="chart-header">Динамика стоимости юнита по стратегии</h2>
    <div v-if="chartData?.length > 0" class="chart-data">
      <div class="chart-unit-price" v-show="unitPrice && priceDate">
        <label>{{ `Стоимость юнита (на ${priceDate})` }}</label>
        <span>{{ `${unitPrice} ₽` }}</span>
      </div>
      <div class="chart-profit">
        <label>{{ `Доходность за период` }}</label>
        <span v-show="profitComputed">{{ profitComputed }}</span>
      </div>
    </div>
    <div v-if="!(chartData?.length > 0)" style="margin-top: 80px; margin-bottom: 110px;">
      За данный период нет данных для отображения графика
    </div>
    <div class="chart-menu-wrapper">
      <div class="chart-menu">
        <span class="chart-btn" :class="{'chart-btn_selected': dateSelector === 7}" @click="btnClick(7)">
          Неделя
        </span>
        <span class="chart-btn" :class="{'chart-btn_selected': dateSelector === 30}" @click="btnClick(30)">
          Месяц
        </span>
        <span class="chart-btn" :class="{'chart-btn_selected': dateSelector === 180}" @click="btnClick(180)">
          Полгода
        </span>
        <span class="chart-btn" :class="{'chart-btn_selected': dateSelector === 365}" @click="btnClick(365)">
          Год
        </span>
        <span class="chart-btn" :class="{'chart-btn_selected': dateSelector === 10000}" @click="btnClick(10000)">
         Весь срок
        </span>
      </div>
      <div class="chart-search">
        <span>Период</span>
        <span>с</span>
        <input @input="inputHandle" v-model="search_begin_date" type="date">
        <span>по</span>
        <input @input="inputHandle" v-model="search_end_date" type="date">
      </div>
    </div>
    <canvas id="acquisitions"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { formatDate } from "@/profile/composables/utils";
import { localDevelopment, testDevelopment } from "@/profile/composables/url";
import { unitQuotes } from "@/profile/composables/mockData";

export default {
  name: "ChartNewComponent",
  props: {},
  data: () => {
    return {
      chart: null,
      dateSelector: 10000, // 10000 = весь срок
      search_begin_date: null,
      search_end_date: null,
      unitPrice: null,
      priceDate: null,
      menuBtnDisabled: false,
      searchTimeout: null,
      chartData: []
    };
  },
  computed: {
    profitComputed() { // ( (итог - первый) / первый ) * 100% + оболочка для 2 знаков после запятой
      return this.chartData?.length >= 2 ? `${Math.round(((this.chartData[this.chartData.length - 1].quote - this.chartData[0].quote) / this.chartData[0].quote) * 100 * 100) / 100} %` : ``;
    }
  },
  methods: {
    getChart() {
      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(
        document.getElementById("acquisitions"),
        {
          type: "line",
          data: {
            labels: this.chartData?.length > 0 ? this.chartData.map(row => row.quotesDate) : [],
            datasets: [
              {
                label: "Стоимость юнита",
                data: this.chartData?.length > 0 ? this.chartData.map(row => row.quote) : [],
                borderColor: "#007bff",
                backgroundColor: "rgba(46,74,220,0.5)",
                pointStyle: "circle",
                pointRadius: 3,
                pointHoverRadius: 5,
                lineTension: 0.3,
                fill: true
              }
            ]
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                // beginAtZero: true,
                min: this.chartData?.length > 0 ? Math.min(...this.chartData.map(row => row.quote)) - 200 : 0, // Устанавливаем минимальное значение
                max: this.chartData?.length > 0 ? Math.max(...this.chartData.map(row => row.quote)) + 200 : 0 // Устанавливаем максимальное значение
                // ticks: {
                //   maxTicksLimit: 7
                // }
              },
              x: {
                ticks: {
                  autoSkip: true, // Автоматически пропускать метки, если их слишком много
                  maxTicksLimit: 5
                }
              }
            }
          }
        }
      );
      this.$emit("getChartData", this.chartData);
    },
    async getData(arg) {
      let b_date;
      let e_date;
      if (arg !== "search") { // график по кнопкам
        let x = new Date();
        b_date = new Date(x.setDate(x.getDate() - arg)).toISOString().slice(0, 10);
        e_date = new Date().toISOString().slice(0, 10);
      } else { // график по периоду инпутов
        b_date = this.search_begin_date;
        e_date = this.search_end_date;
        this.dateSelector = null;
      }

      const contract = this.$myApp.contract.get();
      const api = await this.$api2.get({
        action: "get-contract-unit-quotes",
        object: "cpa",
        // strategy_id: "1646463994", TODO нужно передавать для промо страниц стратегий
        agreement_id: this.$route.params.token ? this.$route.params.id : contract.agreement_id,
        contract_id: this.$route.params.token ? this.$route.params.id : contract.contract_id,
        version_id: this.$route.params.token ? this.$route.params.id : contract.version_id,
        cover_id: this.$route.params.token ? this.$route.params.id : contract.cover_id,
        document_id: this.$route.params.token ? this.$route.params.id : contract.document_id,
        begin_date: b_date,
        end_date: e_date,
        token: this.$route.params.token ? this.$route.params.token : this.$myApp.token.get()
      });
      if (!localDevelopment && !testDevelopment) { // на тесте нету данных для графика, поэтому такая конструкция
        this.chartData = api?.data?.unit_quotes?.data?.quotes?.map(el => {
          return {
            quote: Math.round(el.quote * 1000) / 1000,
            quotesDate: formatDate(el.quotesdate)
          };
        });
      } else {
        this.chartData = unitQuotes.map(el => {
          return {
            quote: Math.round(el.quote * 1000) / 1000,
            quotesDate: formatDate(el.quotesdate)
          };
        });
      }
      this.priceDate = api?.data?.unit_quotes?.data?.pricedate ? formatDate(api.data.unit_quotes.data.pricedate) : null;
      this.unitPrice = api?.data?.unit_quotes?.data?.price ? Math.round(api.data.unit_quotes.data.price * 100) / 100 : null;
      if (api.success) {
        await this.getChart();
      }
    },
    async btnClick(arg) {
      if (this.dateSelector !== arg && !this.menuBtnDisabled) {
        this.dateSelector = arg;
        this.search_begin_date = null;
        this.search_end_date = null;
        await this.getData(arg);

        this.menuBtnDisabled = true;
        setTimeout(() => {
          this.menuBtnDisabled = false;
        }, 1000);
      }
    },
    inputHandle() {
      const checkDate = new Date("1970-01-01");
      const beginDate = new Date(this.search_begin_date);
      const endDate = new Date(this.search_end_date);

      if (beginDate > checkDate && endDate > checkDate) { // заглушка, чтобы ограничить запросы в момент выбора даты
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
          this.getData("search");
        }, 1500); // заглушка, чтобы построение графика шло не на каждый символ
      }
    }
  },
  created() {
    this.getData(10000); // 10000 = весь срок
  }
};
</script>

<style scoped>
.chart-header {
  font-family: SourceSansPro, sans-serif;
  font-size: 24px;
  color: #212325;
  margin-bottom: 60px;
  margin-top: 0;
  font-weight: 700;
}

.chart-data {
  margin-bottom: 64px;
  display: flex;
  flex-direction: row;
}

.chart-data .chart-unit-price {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-radius: 12px;
  background: #007bff;
  color: white;
  max-width: fit-content;
  margin-right: 20px;
}

.chart-data .chart-profit {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-radius: 12px;
  background: #007bff;
  color: white;
  max-width: fit-content;
}

.chart-data .chart-unit-price label {
  margin-bottom: 20px;
}

.chart-data .chart-unit-price span {
  font-weight: 600;
}

.chart-menu-wrapper {
  display: flex;
  flex-direction: row;
}

.chart-menu {
  background: #f2f4f7;
  border-radius: 8px;
  display: flex;
  margin: 0 30px 20px 0;
  padding: 5px 10px;
  max-width: fit-content;
}

.chart-search {
  background: #f2f4f7;
  border-radius: 8px;
  display: flex;
  margin: 0 0 20px 0;
  padding: 5px 10px;
  align-items: center;
  max-width: 420px;
  width: 100%;
  justify-content: space-between;
}

.chart-search input {
  background-color: #f2f4f7;
  border: 1px solid #6f7072;
  width: 37%;
}

.chart-btn {
  cursor: pointer;
  padding: 10px 15px;
}

.chart-btn_selected {
  background-color: white;
}

@media only screen and (max-width: 1000px) {
  .chart-btn {
    padding: 10px;
    font-size: 16px
  }

  .chart-header {
    font-size: 20px;
  }
}

@media only screen and (max-width: 579px) {
  .chart-btn {
    padding: 8px;
    font-size: 12px
  }

  .chart-data .chart-unit-price label {
    font-size: 16px
  }

  .chart-menu-wrapper {
    flex-direction: column;
  }

  .chart-menu {
    margin: 0 0 20px 0;
    max-width: unset;
    justify-content: space-between;
  }

  .chart-search {
    display: flex;
    font-size: 12px;
    padding: 10px;
    flex-wrap: unset;
    max-width: unset;
    width: unset;
  }

  .chart-search input {
    width: 39%;
  }
}

@media only screen and (max-width: 400px) {
  .chart-search input {
    width: 37%;
  }
}
</style>